import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bug, Check, Globe, LogOut, Puzzle, RefreshCw, SunMoon, User } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { openBugReport } from '../BugReportButton';
import { useExtensionInstallStore } from '../ExtensionInstall';
import { useLanguageToggle } from '../LanguageSwitcher';
import { runUpdateCheck } from '../UpdateCheckButton';
import { useTheme } from '../../providers/ThemeProvider';
import { THEME_PREFERENCES } from '../../providers/themeModel';
import Menu, { type MenuItemDef } from './Menu';
import Popover from './Popover';

/**
 * The one user menu (D4, PRD §4.3, T-30): the top-bar avatar. It holds what
 * used to be five permanent top-bar icons plus the sidebar profile row:
 * פרופיל · שפה · מראה · בדיקת עדכונים · דיווח על באג · התנתקות.
 * The "עוד" sheet on mobile shows the same items (`useAccountItems`).
 * Attendance moved out of it into the top bar (`attendance/AttendanceControl`,
 * owner 2026-10-11).
 */

const ICON = { size: 16, strokeWidth: 1.75 } as const;

/** The account items, shared by the avatar menu and the mobile "עוד" sheet. */
export function useAccountItems(onLogout: () => void): MenuItemDef[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useLanguageToggle();
  const theme = useTheme();
  // C-30 (clarity, N-04): the extension left the top bar; its item lives here.
  const extension = useExtensionInstallStore();

  const items: (MenuItemDef | false)[] = [
    { key: 'profile', label: t('shell.userMenu.profile'), icon: <User {...ICON} />, onSelect: () => navigate('/profile') },
    {
      key: 'language',
      label: language.endonym,
      textValue: language.endonym,
      lang: language.target,
      ariaLabel: language.hint,
      icon: <Globe {...ICON} />,
      onSelect: () => { void language.toggle(); },
    },
    // Appearance (T-53, Q-015): one row, "מראה" + מערכת · בהיר · כהה (menuitemradio).
    ...THEME_PREFERENCES.map((pref): MenuItemDef => ({
      key: `appearance-${pref}`,
      label: t(`appearance.${pref}`),
      ariaLabel: pref === 'system' ? t('appearance.systemHint') : undefined,
      radioGroup: { id: 'appearance', label: t('appearance.label'), icon: <SunMoon {...ICON} /> },
      checked: theme.preference === pref,
      keepOpen: true,
      onSelect: () => theme.setPreference(pref),
      data: { 'data-appearance': pref },
    })),
    { key: 'updates', label: t('shell.userMenu.updates'), icon: <RefreshCw {...ICON} />, onSelect: runUpdateCheck, separatorBefore: true },
    (extension.status === 'install' || extension.status === 'update') && {
      key: 'extension',
      label: extension.status === 'update' ? t('extensionInstall.update') : t('extensionInstall.install'),
      icon: <Puzzle {...ICON} />,
      onSelect: () => extension.install(),
      data: { 'data-testid': 'extension-menu-install' },
    },
    extension.status === 'installed' && {
      key: 'extension-installed',
      label: t('extensionInstall.installed', { version: extension.version ?? '' }),
      icon: <Check {...ICON} />,
      disabled: true,
      data: { 'data-testid': 'extension-installed' },
    },
    { key: 'bug', label: t('shell.userMenu.bugReport'), icon: <Bug {...ICON} />, onSelect: openBugReport },
    { key: 'my-bugs', label: t('bugFlow.myReports'), icon: <Bug {...ICON} />, onSelect: () => navigate('/my-bug-reports') },
    { key: 'logout', label: t('shell.userMenu.logout'), icon: <LogOut {...ICON} className="rtl-mirror" />, onSelect: onLogout, separatorBefore: true },
  ];
  return items.filter((i): i is MenuItemDef => !!i);
}

/**
 * The appearance row outside a menu (the mobile "עוד" sheet, a dialog): the same
 * radio items as one row, as a WAI-ARIA radio group (roving focus, arrows select).
 */
export function AccountRadioRow({ items }: { items: MenuItemDef[] }) {
  const labelId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const group = items[0]?.radioGroup;
  if (!group) return null;
  const checkedAt = Math.max(0, items.findIndex((i) => i.checked));
  const choose = (k: number) => {
    const next = (k + items.length) % items.length;
    items[next].onSelect?.();
    refs.current[next]?.focus();
  };
  return (
    <li className="flex min-h-touch items-center gap-3 px-3">
      <span className="flex size-5 flex-shrink-0 items-center justify-center text-text-3" aria-hidden="true">{group.icon}</span>
      <span id={labelId} className="flex-1 truncate text-start text-base text-text-2">{group.label}</span>
      <span
        role="radiogroup"
        aria-labelledby={labelId}
        className="flex flex-shrink-0 items-center gap-0.5 rounded-lg bg-surface-subtle p-0.5"
      >
        {items.map((item, k) => (
          <button
            key={item.key}
            ref={(el) => { refs.current[k] = el; }}
            type="button"
            role="radio"
            aria-checked={!!item.checked}
            aria-label={item.ariaLabel}
            tabIndex={k === checkedAt ? 0 : -1}
            onClick={() => item.onSelect?.()}
            onKeyDown={(e) => {
              const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
              const forward = e.key === 'ArrowDown' || e.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
              const back = e.key === 'ArrowUp' || e.key === (rtl ? 'ArrowRight' : 'ArrowLeft');
              if (!forward && !back) return;
              e.preventDefault();
              choose(checkedAt + (forward ? 1 : -1));
            }}
            className={`min-h-touch rounded-md px-3 text-sm font-medium transition-colors duration-micro ${item.checked ? 'bg-surface text-text shadow-xs' : 'text-text-2 hover:text-text'}`}
            {...(item.data ?? {})}
          >
            {item.label}
          </button>
        ))}
      </span>
    </li>
  );
}

export function Avatar({ name, className = '' }: { name?: string | null; className?: string }) {
  const initial = (name ?? '').trim().charAt(0).toUpperCase() || '·';
  return (
    <span
      className={`inline-flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-800 ${className}`}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

export default function AvatarMenu({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const { user, organization } = useAuthStore();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const headingId = useId();
  const items = useAccountItems(onLogout);

  const dismiss = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('shell.userMenu.trigger')}
        title={user?.name ?? undefined}
        data-avatar-menu-trigger=""
        className="relative flex size-touch items-center justify-center rounded-full transition-transform duration-press active:scale-95"
      >
        <Avatar name={user?.name} />
      </button>
      <Popover
        open={open}
        onClose={(reason) => dismiss(reason !== 'outside')}
        anchorRef={triggerRef}
        align="end"
        className="w-72 p-1.5"
      >
        <div className="flex items-center gap-3 px-2.5 pb-2.5 pt-2" id={headingId}>
          <Avatar name={user?.name} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-text">{user?.name}</p>
            <p className="truncate text-xs text-text-2">
              {user?.email ? <bdi dir="ltr">{user.email}</bdi> : organization?.name}
            </p>
          </div>
        </div>
        <div className="mx-2 mb-1 h-px bg-border" aria-hidden="true" />
        <Menu id={menuId} items={items} labelledBy={headingId} onDismiss={() => dismiss(true)} />
      </Popover>
    </>
  );
}
